/**
 * Where a voice refusal keeps its machine-readable half, and whether a
 * transcript is worth a request at all. Both as pure functions.
 *
 * `src/store/index.js` cannot be loaded in a plain node process — it builds a
 * Vuex store, reads `window.location` at module scope and calls `fetch` — so
 * the two decisions inside it most likely to be wrong live here instead, where
 * `scripts/test-voice-refusal.js` can exercise them directly. No browser
 * globals, no I/O, no timers.
 *
 * Neither function throws, ever. A classifier that can throw would replace a
 * specific, actionable server refusal ("VP1 matches 2 pucks") with the generic
 * frontend one at exactly the moment the user needs the specific one.
 */

/**
 * The server's own bound (`voiceParseController.js:29`, `MAX_TRANSCRIPT_CHARS`).
 * Repeated here so the client can decline before spending a request and a model
 * turn rather than after a 400.
 */
export const MAX_TRANSCRIPT_CHARS = 500;

/**
 * A structured detail is an object with named fields — `{identifier,
 * candidates}` for an ambiguous puck, `{boxId, candidates, locations}` for an
 * ambiguous box. An array is rejected along with strings and numbers: every
 * reader asks it for a named field, and an array would answer `undefined` to
 * all of them while still looking like a detail to a truthiness check.
 */
function isStructuredDetail(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Pull the server's refusal `code` and `detail` out of an error body.
 *
 * The same two fields arrive at two depths, because two Express error handlers
 * emit them (`inventory-backend/controllers/errorController.js`):
 *
 *   development   { status, error: { code, detail, ... }, message, stack }
 *   production    { status, message, code, detail }
 *
 * and production before packet P1.0c sends neither. So both depths are read
 * rather than one being pinned, and a body carrying neither is not an error —
 * it is the ordinary shape of most refusals, and answers with two nulls.
 *
 * `status` is part of the fixed signature but is not consulted: `voiceFetch`
 * also throws on a 2xx whose body says `status: "fail"`, so what makes a body a
 * refusal is its shape, not its code.
 *
 * @param {number} status HTTP status of the response the body came from.
 * @param {object|null} body Parsed JSON body, or null for a non-JSON response.
 * @returns {{ code: string|null, detail: object|null }}
 */
export function classifyRefusal(status, body) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { code: null, detail: null };
  }

  const nested = isStructuredDetail(body.error) ? body.error : null;
  const code = body.code ?? nested?.code;
  const detail = body.detail ?? nested?.detail;

  return {
    // A code is a string constant the client compares against
    // (`AMBIGUOUS_IDENTIFIER`). Anything else — a number, an object, an empty
    // string — is not one, and saying so with null is better than handing a
    // caller something that will never match and never explain why.
    code: typeof code === "string" && code ? code : null,
    detail: isStructuredDetail(detail) ? detail : null,
  };
}

/**
 * Decide whether a transcript can be sent to `/api/v1/voice/parse`.
 *
 * The bound is the server's, and it is checked here so an edited transcript
 * that is empty or pasted-document long is refused without a round trip. The
 * trimmed text comes back either way: it is what a caller should send, and on a
 * rejection it is what the caller should show.
 *
 * @param {*} text Anything; a non-string is treated as no transcript.
 * @returns {{ ok: boolean, transcript: string }}
 */
export function validateTranscript(text) {
  const transcript = typeof text === "string" ? text.trim() : "";
  const ok = transcript.length > 0 && transcript.length <= MAX_TRANSCRIPT_CHARS;
  return { ok, transcript };
}
